import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, MapPin, User, CheckCircle, Circle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/contexts/TenantContext';
import { useTenantTimezone } from '@/hooks/useTenantTimezone';

interface TimelineEvent {
  id: string;
  status: string;
  timestamp: string;
  location?: { lat: number; lng: number };
  notes?: string;
  driverName?: string;
  stopRole?: 'origin' | 'destination' | null;
  stopLocationName?: string | null;
}

interface TaskTimelineProps {
  taskId: string;
}

export const TaskTimeline: React.FC<TaskTimelineProps> = ({ taskId }) => {
  const { currentTenant } = useTenant();
  const { formatDateTimeInTenantTimezone } = useTenantTimezone();
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!taskId || !currentTenant) return;
    
    fetchTimeline();
    
    // Set up real-time subscription for task history updates
    const channel = supabase
      .channel('task-timeline-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'task_history',
          filter: `task_id=eq.${taskId}`
        },
        () => {
          fetchTimeline();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks',
          filter: `id=eq.${taskId}`
        },
        () => {
          fetchTimeline();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [taskId, currentTenant]);

  const fetchTimeline = async () => {
    try {
      // Get task history
      const { data: taskHistory, error: historyError } = await supabase
        .from('task_history')
        .select(`
          id,
          status,
          timestamp,
          location,
          notes,
          driver_id,
          stop_id
        `)
        .eq('task_id', taskId)
        .eq('tenant_id', currentTenant.id)
        .order('timestamp', { ascending: false });

      if (historyError) throw historyError;

      // Phase 3B: enrich rows tied to a stop with stop role / location name
      const stopIds = Array.from(
        new Set(((taskHistory ?? []) as any[]).map((r) => r.stop_id).filter(Boolean)),
      );
      const stopMetaById = new Map<
        string,
        { role: 'origin' | 'destination'; location_name: string | null }
      >();
      if (stopIds.length > 0) {
        const { data: stopRows } = await supabase
          .from('task_stops')
          .select('id, stop_role, stop_sequence, location_name')
          .in('id', stopIds);
        (stopRows || []).forEach((s: any) => {
          const seq = typeof s.stop_sequence === 'number' ? s.stop_sequence : null;
          const role: 'origin' | 'destination' =
            s.stop_role === 'destination'
              ? 'destination'
              : s.stop_role === 'origin'
                ? 'origin'
                : seq && seq > 1
                  ? 'destination'
                  : 'origin';
          stopMetaById.set(s.id, { role, location_name: s.location_name ?? null });
        });
      }

      // Also get the current task details for the initial creation
      const { data: task, error: taskError } = await supabase
        .from('tasks')
        .select(`
          created_at,
          status,
          timestamp_accepted,
          timestamp_started,
          timestamp_arrived,
          timestamp_completed,
          assigned_driver_id
        `)
        .eq('id', taskId)
        .maybeSingle();

      if (taskError) throw taskError;

      if (!task) {
        console.warn('Task not found:', taskId);
        setTimeline([]);
        return;
      }

      const events: TimelineEvent[] = [];

      // Get driver name if available
      let driverName: string | undefined;
      if (task.assigned_driver_id) {
        const { data: driverProfile } = await supabase
          .from('driver_profiles')
          .select(`
            user_id,
            user:app_users(name, first_name, last_name, email)
          `)
          .eq('id', task.assigned_driver_id)
          .maybeSingle();
        
        if (driverProfile?.user) {
          const user = driverProfile.user as any;
          driverName = user.name || 
                      (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : null) ||
                      user.first_name || 
                      user.last_name ||
                      user.email ||
                      undefined;
        }
      }

      // Add task creation event
      events.push({
        id: 'created',
        status: 'created',
        timestamp: task.created_at,
        notes: 'Task created'
      });

      // Add status timestamp events
      if (task.timestamp_accepted) {
        events.push({
          id: 'accepted',
          status: 'accepted',
          timestamp: task.timestamp_accepted,
          driverName: driverName,
          notes: 'Task accepted by driver'
        });
      }

      if (task.timestamp_started) {
        events.push({
          id: 'started',
          status: 'en_route',
          timestamp: task.timestamp_started,
          driverName: driverName,
          notes: 'Driver started journey'
        });
      }

      if (task.timestamp_arrived) {
        events.push({
          id: 'arrived',
          status: 'arrived',
          timestamp: task.timestamp_arrived,
          driverName: driverName,
          notes: 'Driver arrived at location'
        });
      }

      if (task.timestamp_completed) {
        events.push({
          id: 'completed',
          status: 'completed',
          timestamp: task.timestamp_completed,
          driverName: driverName,
          notes: 'Task completed'
        });
      }

      // Add history events (these take precedence over timestamp events)
      taskHistory?.forEach((event: any) => {
        const meta = event.stop_id ? stopMetaById.get(event.stop_id) : null;
        events.push({
          id: event.id,
          status: event.status,
          timestamp: event.timestamp,
          location: event.location ? {
            lat: parseFloat(String(event.location).split(',')[0].slice(1)),
            lng: parseFloat(String(event.location).split(',')[1].slice(0, -1))
          } : undefined,
          notes: event.notes,
          driverName: driverName,
          stopRole: meta?.role ?? null,
          stopLocationName: meta?.location_name ?? null,
        });
      });

      // Sort by timestamp (newest first) and remove duplicate statuses, keeping the newest
      events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      // Remove duplicate status events, keeping only the most recent one
      // for each status. Stop-scoped events are keyed by (status, stopRole)
      // so origin-complete and destination-complete are both preserved.
      const seenKeys = new Set<string>();
      const filteredEvents = events.filter(event => {
        const key = event.stopRole ? `${event.status}::${event.stopRole}` : event.status;
        if (seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
      });

      setTimeline(filteredEvents);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'delivered':
      case 'handed_over':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'pending':
      case 'created':
        return <Circle className="w-5 h-5 text-gray-400" />;
      case 'picked_up':
        return <CheckCircle className="w-5 h-5 text-orange-600" />;
      default:
        return <Clock className="w-5 h-5 text-blue-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'delivered':
      case 'handed_over':
        return 'bg-green-100 border-green-200';
      case 'failed':
        return 'bg-red-100 border-red-200';
      case 'pending':
      case 'created':
        return 'bg-gray-100 border-gray-200';
      case 'picked_up':
        return 'bg-orange-100 border-orange-200';
      default:
        return 'bg-blue-100 border-blue-200';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Loading timeline...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Task Timeline
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {timeline.map((event, index) => (
            <div key={event.id} className="flex gap-4">
              {/* Timeline Icon */}
              <div className="flex flex-col items-center">
                <div className={`p-2 rounded-full border-2 ${getStatusColor(event.status)}`}>
                  {getStatusIcon(event.status)}
                </div>
                {index < timeline.length - 1 && (
                  <div className="w-0.5 h-8 bg-border mt-2" />
                )}
              </div>

              {/* Event Details */}
              <div className="flex-1 pb-4">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge variant="outline" className="capitalize">
                    {event.status.replace('_', ' ')}
                  </Badge>
                  {event.stopRole && (
                    <Badge variant="secondary" className="text-xs capitalize">
                      {event.stopRole}
                      {event.stopLocationName ? ` · ${event.stopLocationName}` : ''}
                    </Badge>
                  )}
                  <span className="text-sm text-muted-foreground">
                    {formatDateTimeInTenantTimezone(event.timestamp, 'MMM d, yyyy HH:mm:ss')}
                  </span>
                </div>

                {event.notes && (
                  <p className="text-sm text-foreground mb-2">{event.notes}</p>
                )}

                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                  {event.driverName && (
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <span>{event.driverName}</span>
                    </div>
                  )}
                  {event.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      <span>
                        {event.location.lat.toFixed(6)}, {event.location.lng.toFixed(6)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {timeline.length === 0 && (
            <div className="text-center text-muted-foreground py-8">
              No timeline events available for this task.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};